<template>
  <div
    class="quiz-page"
    :class="{ 'has-safe-top': needsSafeTop, 'is-full-screen': isFullScreen, 'is-my-bets': activeTab === 'myBets', 'is-lottery': activeTab === 'lottery' }"
    :style="pageStyle"
  >
    <div class="quiz-page__flag-preload" aria-hidden="true">
      <img
        v-for="(src, index) in preloadedTeamFlags"
        :key="index"
        :src="src"
        alt=""
      >
    </div>
    <quiz-state
      v-if="pageState === 'loading'"
      state="loading"
      @rules="openRules"
      @back="closeWebView"
    />
    <quiz-state
      v-else-if="pageState === 'error'"
      state="error"
      :title="errorTitle"
      :text="errorText"
      retry
      @retry="bootstrap"
      @rules="openRules"
      @back="closeWebView"
    />
    <blocked
      v-else-if="pageState === 'blocked'"
      @customer-service="openCustomerService"
      @rules="openRules"
      @back="closeWebView"
    />
    <template v-else>
      <div v-if="activeTab !== 'myBets'" class="quiz-page__top">
        <button class="quiz-page__back" @click="closeWebView"></button>
        <div class="quiz-page__title">世界杯竞猜</div>
        <button class="quiz-page__rules" @click="openRules">规则&好礼</button>
      </div>
      <div v-if="activeTab !== 'myBets'" class="quiz-page__hero">
        <div class="quiz-page__hero-brand">世界杯竞猜</div>
        <div class="quiz-page__hero-sub">按场次竞猜 · 猜中抽好礼</div>
      </div>
      <div class="quiz-page__body">
        <keep-alive>
          <hall
            v-if="keepHall"
            v-show="activeTab === 'hall'"
            :entry="entry"
            @refresh-entry="refreshEntry"
            @bet-success="handleBetSuccess"
            @go-lottery="switchTab('lottery')"
            @recharge="openRecharge"
            @blocked="pageState = 'blocked'"
            @preload-flags="preloadTeamFlags"
          />
        </keep-alive>
        <my-bets
          v-if="activeTab === 'myBets' || (activeTab === 'rules' && previousTab === 'myBets')"
          v-show="activeTab === 'myBets'"
          @back="closeWebView"
          @rules="openRules"
        />
        <lottery
          v-else-if="activeTab === 'lottery'"
          @rules="openRules"
          @remaining-change="updateRemaining"
          @blocked="pageState = 'blocked'"
        />
      </div>
      <div v-if="activeTab === 'rules'" class="quiz-page__rules-layer">
        <rules @back="backFromRules" />
      </div>
      <div class="quiz-page__tabs">
        <button aria-label="竞猜大厅" :class="{ active: activeTab === 'hall' }" @click="switchTab('hall')">
          <img class="quiz-page__tab-icon" :src="tabIcon('hall')" alt="">
        </button>
        <button aria-label="我的竞猜" :class="{ active: activeTab === 'myBets' }" @click="switchTab('myBets')">
          <img class="quiz-page__tab-icon" :src="tabIcon('myBets')" alt="">
        </button>
        <button aria-label="抽奖中心" :class="{ active: activeTab === 'lottery' }" @click="switchTab('lottery')">
          <img class="quiz-page__tab-icon" :src="tabIcon('lottery')" alt="">
          <i v-if="remainingDraw > 0">{{ remainingDraw }}</i>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { getEntry, rechargeMockBalance } from '@/api/worldcupQuiz'
import NativeBridge from '@/utils/NativeBridge'
import Blocked from './pages/Blocked.vue'
import Hall from './pages/Hall.vue'
import Lottery from './pages/Lottery.vue'
import MyBets from './pages/MyBets.vue'
import QuizState from './components/QuizState.vue'
import Rules from './pages/Rules.vue'
import { isMockMode, unwrapResponse } from './utils'
import { preloadTeamLogos } from '../worldcup2026/utils/teamFlag'
import tabHallInactive from '@/assets/image/worldcupQuiz/tab-hall-inactive.png'
import tabHallActive from '@/assets/image/worldcupQuiz/tab-hall-active.png'
import tabBetsInactive from '@/assets/image/worldcupQuiz/tab-bets-inactive.png'
import tabBetsActive from '@/assets/image/worldcupQuiz/tab-bets-active.png'
import tabLotteryInactive from '@/assets/image/worldcupQuiz/tab-lottery-inactive.png'
import tabLotteryActive from '@/assets/image/worldcupQuiz/tab-lottery-active.png'

const TAB_ICONS = {
  hall: {
    active: tabHallActive,
    inactive: tabHallInactive
  },
  myBets: {
    active: tabBetsActive,
    inactive: tabBetsInactive
  },
  lottery: {
    active: tabLotteryActive,
    inactive: tabLotteryInactive
  }
}

const TOKEN_TIMEOUT = 8000

export default {
  name: 'WorldCupQuiz',
  components: { Blocked, Hall, Lottery, MyBets, QuizState, Rules },
  data () {
    return {
      nativeBridge: new NativeBridge(),
      pageState: 'loading',
      activeTab: 'hall',
      previousTab: 'hall',
      keepHall: false,
      token: '',
      entry: {},
      remainingDraw: 0,
      preloadedTeamFlags: [],
      safeTop: 0,
      navHeight: 48,
      navBgOpacity: 0,
      errorTitle: '网络开小差了，加载失败',
      errorText: '请稍后重试，仍失败请退出后重新进入'
    }
  },
  computed: {
    isMostOneApp () {
      return typeof window.MostOneJSApis !== 'undefined' || !!(window.webkit && window.webkit.messageHandlers)
    },
    needsSafeTop () {
      return this.$route.query.screen === 'full' || this.isMostOneApp
    },
    isFullScreen () {
      return this.$route.query.screen === 'full'
    },
    pageStyle () {
      return {
        '--quiz-safe-top': `${this.safeTop}px`,
        '--quiz-nav-height': `${this.navHeight}px`,
        '--quiz-nav-bg-opacity': this.activeTab === 'hall' ? this.navBgOpacity : 0
      }
    }
  },
  created () {
    document.body.classList.add('worldcup-quiz-body')
    this.bootstrap()
  },
  mounted () {
    window.addEventListener('scroll', this.updateNavBgOpacity, { passive: true })
    this.updateNavBgOpacity()
  },
  beforeDestroy () {
    document.body.classList.remove('worldcup-quiz-body')
    window.removeEventListener('scroll', this.updateNavBgOpacity)
  },
  methods: {
    tabIcon (tab) {
      const icons = TAB_ICONS[tab]
      return icons[this.activeTab === tab ? 'active' : 'inactive']
    },
    preloadTeamFlags (sources) {
      this.preloadedTeamFlags = Array.isArray(sources) ? sources : []
      preloadTeamLogos(this.preloadedTeamFlags)
    },
    async bootstrap () {
      this.pageState = 'loading'
      this.errorTitle = '网络开小差了，加载失败'
      this.errorText = '请稍后重试，仍失败请退出后重新进入'
      this.activeTab = this.$route.query.tab || 'hall'
      this.updateSafeTop()
      await this.waitForNextFrame()
      if (isMockMode() && this.$route.query.mockLoading === '1') return
      try {
        await this.prepareToken(true)
        await this.refreshEntry()
        if (this.entry.in_blacklist) {
          this.pageState = 'blocked'
          return
        }
        this.pageState = 'ready'
        this.keepHall = true
      } catch (error) {
        this.applyBootstrapError(error)
        this.pageState = 'error'
      }
    },
    async prepareToken (force = false) {
      if (isMockMode()) {
        this.token = 'mock-token'
        window.$userToken = this.token
        return
      }
      if (!force && window.$userToken) {
        this.token = window.$userToken
        return
      }
      const res = await this.waitForBridge(this.nativeBridge.sendMessage('getUserTokenToApp'), TOKEN_TIMEOUT)
      if (!res || Number(res.code) !== 200 || !res.data || !res.data.token) {
        throw new Error('登录状态获取失败，请退出后重新进入')
      }
      this.token = res.data.token
      window.$userToken = this.token
    },
    applyBootstrapError (error) {
      if (!error) return
      this.errorTitle = '暂无法进入活动'
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        this.errorTitle = '网络开小差了，加载失败'
        this.errorText = '网络异常，请稍后重试'
        return
      }
      this.errorText = error.message || '请稍后重试，仍失败请退出后重新进入'
    },
    waitForBridge (promise, timeout) {
      return new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => {
          reject(new Error('登录状态获取超时，请退出后重新进入'))
        }, timeout)
        promise.then(res => {
          window.clearTimeout(timer)
          resolve(res)
        }).catch(error => {
          window.clearTimeout(timer)
          reject(error)
        })
      })
    },
    waitForNextFrame () {
      return new Promise(resolve => window.requestAnimationFrame(resolve))
    },
    async refreshEntry () {
      const data = unwrapResponse(await getEntry({ skipToast: true }))
      this.entry = data || {}
      this.remainingDraw = Number(this.entry.remaining_draw) || 0
      return this.entry
    },
    async updateSafeTop () {
      if (!this.needsSafeTop) {
        this.safeTop = 0
        this.navHeight = 48
        return
      }
      const scale = this.isFullScreen ? ((window.innerHeight || 812) / 812) : 1
      const isIOS = /iPhone|iPad|iPod/i.test(window.navigator.userAgent || '')
      this.safeTop = Math.round((isIOS ? 44 : 24) * scale)
      this.navHeight = Math.round(48 * scale)
      if (isMockMode()) return
      try {
        const res = await this.nativeBridge.sendMessage('getStatusNavBarToApp')
        if (res && Number(res.code) === 200 && res.data && res.data.statusBar) {
          this.safeTop = Math.max(Math.ceil(Number(res.data.statusBar) / (window.devicePixelRatio || 1)), this.safeTop)
        }
      } catch (error) {}
    },
    switchTab (tab) {
      this.activeTab = tab
      if (tab !== 'rules') this.previousTab = tab
      if (tab === 'lottery' || tab === 'hall') this.refreshEntry().catch(() => {})
      this.$nextTick(this.updateNavBgOpacity)
    },
    updateNavBgOpacity () {
      if (this.activeTab !== 'hall') {
        this.navBgOpacity = 0
        return
      }
      const fadeDistance = Math.max(this.navHeight || 48, 48)
      const progress = Math.min(Math.max(window.scrollY / fadeDistance, 0), 1)
      this.navBgOpacity = Number(progress.toFixed(2))
    },
    openRules () {
      this.previousTab = this.activeTab
      this.activeTab = 'rules'
    },
    backFromRules () {
      this.activeTab = this.previousTab || 'hall'
    },
    updateRemaining (value) {
      this.remainingDraw = Number(value) || 0
      this.entry = Object.assign({}, this.entry, { remaining_draw: this.remainingDraw })
    },
    handleBetSuccess (data) {
      if (data && data.coin_balance !== undefined) {
        this.entry = Object.assign({}, this.entry, { coin_balance: data.coin_balance })
      }
      this.refreshEntry().catch(() => {})
    },
    hasBridgeMethod (method) {
      if (isMockMode()) return true
      return !!(window.MostOneJSApis && typeof window.MostOneJSApis[method] === 'function')
    },
    async openRecharge (done) {
      if (!this.hasBridgeMethod('openRechargeToApp')) {
        Toast('当前版本暂不支持充值，请升级后重试')
        if (typeof done === 'function') done(false)
        return
      }
      try {
        const res = isMockMode()
          ? { code: 200, data: { success: this.$route.query.rechargeFail !== '1' } }
          : await this.nativeBridge.sendMessage('openRechargeToApp', { token: this.token })
        if (!res || Number(res.code) !== 200) {
          Toast((res && res.msg) || '充值暂不可用')
          if (typeof done === 'function') done(false)
          return
        }
        if (res.data && res.data.success === false) {
          Toast('充值未完成')
          if (typeof done === 'function') done(false)
          return
        }
        if (isMockMode()) {
          const mockBalance = Math.max(Number(this.entry.coin_balance) || 0, Number(this.entry.ticket_coin) || 0) + 30
          rechargeMockBalance(mockBalance)
          this.entry = Object.assign({}, this.entry, {
            coin_balance: mockBalance
          })
        } else {
          await this.refreshEntry()
        }
        if (typeof done === 'function') done(true)
      } catch (error) {
        Toast('充值暂不可用')
        if (typeof done === 'function') done(false)
      }
    },
    async openCustomerService () {
      if (!this.hasBridgeMethod('openCustomerServiceToApp')) {
        Toast('当前版本暂不支持在线客服，请升级后重试')
        return
      }
      try {
        if (isMockMode()) {
          Toast('已模拟打开客服')
          return
        }
        await this.nativeBridge.sendMessage('openCustomerServiceToApp', { token: this.token }, false)
      } catch (error) {
        Toast('在线客服暂不可用')
      }
    },
    closeWebView () {
      if (isMockMode()) {
        Toast('已模拟返回')
        return
      }
      this.nativeBridge.sendMessage('quitWebView', {}, false).catch(() => {})
    }
  }
}
</script>

<style lang="scss">
body.worldcup-quiz-body {
  background: #145720;
}
</style>

<style scoped lang="scss">
.quiz-page {
  --quiz-safe-top: 0px;
  --quiz-tab-safe-bottom: min(env(safe-area-inset-bottom, 0px), 12px);
  --quiz-nav-height: 48px;
  max-width: 480px;
  min-height: 100vh;
  margin: 0 auto;
  overflow-x: hidden;
  background: #145720;
  font-size: 14px;
  line-height: 1.4;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.quiz-page.has-safe-top {
  --quiz-safe-top: max(env(safe-area-inset-top, 0px), 24px);
}

.quiz-page__top {
  position: fixed;
  top: 0;
  left: 50%;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 480px;
  height: calc(var(--quiz-safe-top, 0px) + var(--quiz-nav-height, 48px));
  padding: var(--quiz-safe-top, 0px) 18px 0;
  box-sizing: border-box;
  transform: translateX(-50%);
  background: rgba(20, 87, 32, var(--quiz-nav-bg-opacity, 0));
  color: #fff;
  transition: background-color 160ms linear;
}

.quiz-page__back,
.quiz-page__rules {
  border: 0;
  background: transparent;
}

.quiz-page__back {
  position: relative;
  width: 32px;
  height: 32px;
}

.quiz-page__back:before {
  content: "";
  position: absolute;
  top: 9px;
  left: 10px;
  width: 10px;
  height: 10px;
  border-left: 2px solid rgba(255, 255, 255, .92);
  border-bottom: 2px solid rgba(255, 255, 255, .92);
  transform: rotate(45deg);
}

.quiz-page__title {
  opacity: 0;
  font-size: 17px;
  font-weight: 900;
}

.quiz-page__rules {
  height: 32px;
  padding: 0;
  color: rgba(255, 255, 255, .96);
  background: transparent;
  font-size: 14px;
  font-weight: 700;
}

.quiz-page__flag-preload {
  position: fixed;
  left: -1000px;
  top: -1000px;
  z-index: -1;
  overflow: hidden;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.quiz-page__flag-preload img {
  display: block;
  width: 1px;
  height: 1px;
}

.quiz-page__hero {
  min-height: 362px;
  padding: 0;
  box-sizing: border-box;
  text-align: center;
  color: #fff;
  background:
    linear-gradient(180deg, rgba(20, 87, 32, 0) 0%, rgba(20, 87, 32, 0) 88%, #145720 100%),
    url('~@/assets/image/worldcupQuiz/hero.png') center top/100% auto no-repeat;
}

.quiz-page__hero-brand {
  display: none;
  color: #ffe76e;
  text-shadow: 0 3px 0 #0e4a22, 0 8px 16px rgba(0, 0, 0, .45);
  font-size: 42px;
  font-weight: 900;
  line-height: 1.05;
}

.quiz-page__hero-sub {
  display: none;
  margin-top: 10px;
  padding: 5px 12px;
  border: 2px solid rgba(255, 225, 92, .9);
  border-radius: 2px;
  color: #ffe76e;
  background: rgba(20, 87, 32, .88);
  font-size: 13px;
  font-weight: 800;
}

.quiz-page__body {
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 400px);
  margin-top: calc(var(--quiz-safe-top, 0px) + var(--quiz-nav-height, 48px) - 156px);
  border-radius: 22px 22px 0 0;
  background:
    radial-gradient(ellipse at 50% 145px, rgba(255, 240, 155, .12), transparent 34%),
    linear-gradient(180deg, transparent 0, transparent 145px, #145720 145px, #0f421b 100%);
}

.quiz-page__body:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  width: 100%;
  height: 145px;
  pointer-events: none;
  box-sizing: border-box;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, .16) 0%, rgba(255, 255, 255, 0) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.quiz-page__body > * {
  position: relative;
  z-index: 1;
}

.quiz-page.is-my-bets {
  background: #145720;
}

.quiz-page.is-my-bets .quiz-page__body {
  min-height: 100vh;
  margin-top: 0;
  border-radius: 0;
  background: #145720;
}

.quiz-page.is-my-bets .quiz-page__body:before {
  display: none;
}

.quiz-page.is-lottery .quiz-page__body {
  min-height: calc(100vh - 231px);
  margin-top: calc(var(--quiz-safe-top, 0px) + var(--quiz-nav-height, 48px) - 244px);
  border-radius: 22px 22px 0 0;
  background: #145720;
}

.quiz-page.is-lottery .quiz-page__body:before {
  display: none;
}

.quiz-page.is-full-screen.is-lottery .quiz-page__body {
  margin-top: calc(var(--quiz-safe-top, 0px) + var(--quiz-nav-height, 48px) - 244px);
}

.quiz-page__tabs {
  position: fixed;
  left: 50%;
  bottom: 0;
  z-index: 20;
  display: flex;
  width: 100%;
  max-width: 480px;
  height: calc(103px + var(--quiz-tab-safe-bottom));
  box-sizing: border-box;
  transform: translateX(-50%);
  padding-bottom: var(--quiz-tab-safe-bottom);
  border: 2px solid #ffe669;
  border-bottom: 0;
  border-radius: 18px 18px 0 0;
  background:
    linear-gradient(180deg, rgba(0, 84, 15, .08) 0%, rgba(0, 84, 15, .36) 100%),
    url('~@/assets/image/worldcupQuiz/match-card-field.png') center/cover no-repeat;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, .25);
}

.quiz-page__tabs button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  border: 0;
  color: rgba(255, 255, 255, .62);
  background: transparent;
  font-size: 11px;
}

.quiz-page__tabs button.active {
  color: #ffdc4f;
  font-weight: 800;
}

.quiz-page__tab-icon {
  display: block;
  width: 73px;
  height: 73px;
  margin-left: auto;
  margin-right: auto;
  object-fit: contain;
}

.quiz-page__tabs i {
  position: absolute;
  left: calc(50% + 17px);
  top: 23px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 8px;
  color: #fff;
  background: #d93727;
  line-height: 16px;
  font-size: 10px;
  font-style: normal;
}

.quiz-page__rules-layer {
  position: fixed;
  inset: 0;
  z-index: 40;
  max-width: 480px;
  margin: 0 auto;
  overflow: auto;
  background: #145720;
}
</style>
